import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { debounce } from 'lodash';
import { fetchSearch } from '../../apis/article.api';
import CardArticle from '../../components/DiscoveryComponent/MiddleContentComponent/components/card-article.component';
import GoogleAd from '../../components/GoogleAds';
import { Spinner } from '@chakra-ui/react';
import CardArticleLoading from '../../components/DiscoveryComponent/MiddleContentComponent/components/card-article-loading.component';
import { followUser, unfollowUser } from '../../apis/discovery.api';
import { useAtomValue } from 'jotai';
import { profileAtom } from '../../components/Settings/atom';
import CardAuthor from '../../components/DiscoveryComponent/RightContentComponent/components/card-author.component';
import SearchNoResultComponent from '../../components/SearchResult/NoResultComponent';
import GoBackButton from '../../components/GoBackButton';
import { DataLayer } from '../../utils/data-layer';
import SEO from '../../components/SEO';
import { generateSEOMetadata } from '../../utils/seo';

const resultSearchType = [
  {
    name: 'Top',
    value: 'top'
  },
  {
    name: 'Writers',
    value: 'creators'
  },
  {
    name: 'Articles',
    value: 'articles'
  }
];

const SearchResult = () => {
  const [searchParams] = useSearchParams();
  const [searchResult, setSearchResult] = useState([]);
  const [topWriters, setTopWriters] = useState([]);
  const [topArticles, setTopArticles] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    entity: 'top',
    search: searchParams.get('q'),
    totalPage: 0
  });
  const [loading, setLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const [loadingAuthorId, setLoadingAuthorId] = useState(null);
  const navigate = useNavigate();
  const profile = useAtomValue(profileAtom);

  const handleLoadMore = useCallback(
    debounce(() => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY;
      // Increased trigger distance from 200px to 800px for better UX
      if (scrolled >= scrollable - 800 && !loading && pagination.page < pagination.totalPage) {
        setLoading(true);
        setPagination((prevPagination) => ({
          ...prevPagination,
          page: prevPagination.page + 1
        }));
      }
    }, 100),
    [loading, pagination.page, pagination.totalPage]
  );

  useEffect(() => {
    window.addEventListener('scroll', handleLoadMore);
    return () => {
      window.removeEventListener('scroll', handleLoadMore);
      // Cancel any pending debounced calls when component unmounts
      handleLoadMore.cancel();
    };
  }, [handleLoadMore]);

  const renderContentWithAds = () => {
    const contentWithAds = [];
    searchResult.forEach((item, index) => {
      contentWithAds.push(
        <CardArticle
          key={`article-${index}`}
          index={index}
          item={{
            ...item,
            nameCreator: item?.creator?.firstName ?? '',
            imgUrl: item?.creator?.imgUrl
          }}
          isHiddenFeaturedArticles
          isProfile={profile.id === item?.creator?.id}
        />
      );
      if ((index + 1) % 4 === 0) {
        contentWithAds.push(
          <GoogleAd
            articleId={item?.id}
            key={`ad-${index}`}
            slot="1625116786"
            className="my-custom-ad !z-10"
          />
        );
      }
    });
    return contentWithAds;
  };

  const handleFollowUser = async (id, isCurrentlyFollowing) => {
    try {
      setLoadingAuthorId(id);

      // Track writer follow event when following (not unfollowing)
      if (!isCurrentlyFollowing) {
        const author =
          searchResult.find((user) => user.id === id) || topWriters.find((user) => user.id === id);
        if (author?.username) {
          DataLayer.trackWriterFollow(author.username);
        }
      }

      if (isCurrentlyFollowing) {
        await unfollowUser(id);
      } else {
        await followUser(id);
      }
      setSearchResult((prevResults) =>
        prevResults.map((user) =>
          user.id === id ? { ...user, isFollowing: !isCurrentlyFollowing } : user
        )
      );
    } catch (error) {
      console.error('Error updating follow status:', error);
    } finally {
      setLoadingAuthorId(null);
    }
  };

  const handleChangeTab = (item) => {
    setIsSwitching(true);
    setLoading(true);
    // Clear previous results when switching tabs
    setSearchResult([]);
    setTopWriters([]);
    setTopArticles([]);
    setPagination({ ...pagination, entity: item.value, page: 1 });
  };

  useEffect(() => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      search: searchParams.get('q') || '',
      page: 1
    }));
  }, [searchParams, pagination.entity]);

  useEffect(() => {
    const fetchSearchResults = async () => {
      setLoading(true);
      try {
        if (pagination.entity === 'top') {
          // Fetch both writers and articles for the unified "Top" view
          const [writersResult, articlesResult] = await Promise.all([
            fetchSearch({ ...pagination, entity: 'creators', limit: 3 }, profile.id),
            fetchSearch({ ...pagination, entity: 'articles', limit: 8 }, profile.id)
          ]);

          if (writersResult && articlesResult) {
            setTopWriters(writersResult.data || []);
            setTopArticles(articlesResult.data || []);
            // For pagination, we'll use the articles count as the main driver
            setPagination((prevPagination) => ({
              ...prevPagination,
              totalPage: articlesResult.totalPage
            }));
          }
        } else {
          // Original behavior for specific tabs
          const result = await fetchSearch(pagination, profile.id);
          if (result) {
            setSearchResult((prevList) =>
              pagination.page === 1 ? result.data : [...prevList, ...result.data]
            );
            setPagination((prevPagination) => ({
              ...prevPagination,
              totalPage: result.totalPage
            }));
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
        setIsSwitching(false);
      }
    };

    if (pagination.search) fetchSearchResults();
  }, [pagination.page, pagination.search, pagination.entity, profile.id]);

  // Generate SEO metadata for search results page
  const searchQuery = searchParams.get('q') || '';
  const seoMetadata = generateSEOMetadata.searchResults(searchQuery);

  return (
    <div>
      <SEO {...seoMetadata} />
      <div className="w-full flex flex-start flex-col px-4 md:px-0">
        <GoBackButton
          onClick={() => {
            navigate('/');
            window.scrollTo(0, 0);
          }}
        />

        <div className="flex sticky top-[68px] md:top-[80px] pt-4 md:pt-10 z-20 mx-[-16px] bg-white">
          <p className="text-[20px] md:text-[32px] font-[600] w-full mx-auto pl-14 md:pl-0 max-w-[604px] lg:max-w-[804px] text-02-dim-gray">
            Results for{' '}
            <span className="text-growth-green-500 font-[800]">{searchParams.get('q')}</span>
          </p>
        </div>

        <div className="w-full">
          <div className="sticky top-[114px] md:top-[168px] px-4 md:px-0 mx-[-16px] bg-white z-20">
            <div className="flex w-full gap-2 md:gap-4 pt-3 md:pt-6 items-center gap-2 max-w-[804px] mx-auto">
              {resultSearchType?.map((item, index) => (
                <p
                  key={index}
                  className={`rounded-[80px] cursor-pointer font-[800] text-sm px-[14px] py-[8.5px] ${
                    pagination.entity === item.value
                      ? 'bg-[#204A22] text-[#EDF7EE]'
                      : 'bg-[#C8E6C9] text-[#204A22]'
                  }`}
                  onClick={() => handleChangeTab(item)}
                >
                  {item.name}
                </p>
              ))}
            </div>
          </div>

          <div className="mt-6 md:mt-10 flex flex-col gap-5 md:gap-10 max-w-[804px] mx-auto">
            {isSwitching ? (
              <div className="flex justify-center items-center mt-10">
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="green.500"
                  size="lg"
                />
              </div>
            ) : pagination.entity === 'top' ? (
              // Unified "Top" results view
              <div className="flex flex-col gap-6">
                {/* Top Writers Section */}
                {topWriters.length > 0 && (
                  <div className="border-b border-gray-200 pb-8">
                    <div className="flex items-center justify-between mb-6 pb-2 border-b-2 border-growth-green-500">
                      <h2 className="text-xl md:text-2xl font-[800] text-jet-black">Writers</h2>
                      {topWriters.length >= 3 && (
                        <button
                          onClick={() => setPagination({ ...pagination, entity: 'creators' })}
                          className="text-sm text-growth-green-500 hover:text-growth-green-600 font-[700] transition-colors duration-200 flex items-center gap-1"
                        >
                          View all writers
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col gap-4">
                      {topWriters.map((item) => (
                        <CardAuthor
                          key={item.id}
                          author={item}
                          handleFollowUser={handleFollowUser}
                          isLoading={loadingAuthorId === item.id}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Articles Section */}
                {topArticles.length > 0 && (
                  <div className={topWriters.length > 0 ? 'pt-4' : ''}>
                    <div className="flex items-center justify-between mb-6 pb-2 border-b-2 border-growth-green-500">
                      <h2 className="text-xl md:text-2xl font-[800] text-jet-black">Articles</h2>
                      {topArticles.length >= 8 && (
                        <button
                          onClick={() => setPagination({ ...pagination, entity: 'articles' })}
                          className="text-sm text-growth-green-500 hover:text-growth-green-600 font-[700] transition-colors duration-200 flex items-center gap-1"
                        >
                          View all articles
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col gap-[30px]">
                      {topArticles.map((item, index) => (
                        <CardArticle
                          key={`top-article-${index}`}
                          index={index}
                          item={{
                            ...item,
                            nameCreator: item?.creator?.firstName ?? '',
                            imgUrl: item?.creator?.imgUrl
                          }}
                          isHiddenFeaturedArticles
                          isProfile={profile.id === item?.creator?.id}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* No results for Top view */}
                {topWriters.length === 0 && topArticles.length === 0 && !loading && (
                  <SearchNoResultComponent
                    result={searchParams.get('q')}
                    titleBtn="Try different keywords"
                    typeSearchText="content"
                    titleRedirect="refine your search"
                    handleChangeTab={() => {}}
                  />
                )}

                {loading && (
                  <div className="flex flex-col gap-4">
                    {[...Array(2)].map((_, i) => (
                      <div key={`writer-loading-${i}`} className="animate-pulse">
                        <div className="flex items-center gap-4 p-4 bg-gray-100 rounded-lg">
                          <div className="w-12 h-12 bg-gray-300 rounded-full"></div>
                          <div className="flex-1">
                            <div className="h-4 bg-gray-300 rounded w-1/3 mb-2"></div>
                            <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {[...Array(3)].map((_, i) => (
                      <CardArticleLoading key={i} index={i} />
                    ))}
                  </div>
                )}
              </div>
            ) : pagination.entity === 'creators' ? (
              searchResult.length > 0 ? (
                searchResult.map((item) => (
                  <CardAuthor
                    key={item.id}
                    author={item}
                    handleFollowUser={handleFollowUser}
                    isLoading={loadingAuthorId === item.id}
                  />
                ))
              ) : (
                !loading && (
                  <SearchNoResultComponent
                    result={searchParams.get('q')}
                    titleBtn="Browse related Articles"
                    typeSearchText="writers"
                    titleRedirect="check related articles"
                    handleChangeTab={() => setPagination({ ...pagination, entity: 'articles' })}
                  />
                )
              )
            ) : (
              <div className="flex flex-col gap-[30px] 2xl:gap-[60px]">
                {renderContentWithAds()}
                {loading && [...Array(3)].map((_, i) => <CardArticleLoading key={i} index={i} />)}
                {searchResult.length === 0 && !loading && (
                  <SearchNoResultComponent
                    result={searchParams.get('q')}
                    titleBtn="Discover Writers"
                    typeSearchText="articles"
                    titleRedirect="discover expert writers"
                    handleChangeTab={() => setPagination({ ...pagination, entity: 'creators' })}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResult;
